/**
 * Trainova AI — useTenants Hook
 */

import { useState, useEffect, useCallback } from 'react';
import { admin } from '../../../services/api';
import { usePermissions } from './usePermissions';
import { Tenant } from '../types';

export function useTenants() {
  const { isPlatformAdmin } = usePermissions();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(isPlatformAdmin);
  const [error, setError] = useState<string | null>(null);

  const fetchTenants = useCallback(async () => {
    if (!isPlatformAdmin) {
      setIsLoading(false);
      setTenants([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await admin.tenants.list();
      if (res.success && res.data) {
        setTenants(res.data.tenants || []);
      } else {
        setError(res.error?.message || 'Failed to load organizations.');
      }
    } catch {
      setError('An error occurred while fetching organizations.');
    } finally {
      setIsLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const createTenant = async (data: { name: string; slug: string; primaryColor?: string; logoUrl?: string; adminUser?: any }) => {
    const res = await admin.tenants.create(data);
    if (res.success) {
      await fetchTenants();
      return { success: true, data: res.data };
    }
    return { success: false, error: res.error?.message || 'Failed to create organization.' };
  };

  const updateTenant = async (id: string, data: { name?: string; primaryColor?: string; logoUrl?: string; status?: string; isActive?: boolean }) => {
    const res = await admin.tenants.update(id, data);
    if (res.success) {
      await fetchTenants();
      return { success: true, data: res.data };
    }
    return { success: false, error: res.error?.message || 'Failed to update organization.' };
  };

  return {
    tenants,
    isLoading,
    error,
    refresh: fetchTenants,
    createTenant,
    updateTenant,
  };
}
